import {
  type Actor,
  type Anchor,
  type BoardState,
  type Item,
  type ItemOf,
  type PageRect,
} from '@shared/board'
import { clipSize, type Material } from '@shared/material'
import { freeSpotNear } from '@shared/whiteboard/placement'
import { newId } from '@shared/ids'

export const CARD_W = 320
export const MESSAGE_W = 260
export const LINK_W = 300
export const FILE_W = 260

function topZ(s: BoardState): number {
  let z = 0
  for (const i of s.items.values()) if (i.kind !== 'group' && i.z > z) z = i.z
  return z + 1
}

function base(s: BoardState, me: Actor, x: number, y: number, w: number, h: number) {
  const now = Date.now()
  return {
    id: newId(),
    x: Math.round(x),
    y: Math.round(y),
    w,
    h,
    z: topZ(s),
    createdBy: me,
    createdAt: now,
    updatedAt: now,
    version: 0,
  }
}

export function newCard(s: BoardState, me: Actor, x: number, y: number): ItemOf<'card'> {
  return { ...base(s, me, x, y, CARD_W, 120), kind: 'card', data: {} }
}

/** Centre a new box of size w×h on `c`, then nudge it to the nearest free spot. */
export function spotNear(s: BoardState, c: { x: number; y: number }, w: number, h: number) {
  return freeSpotNear(s, { x: Math.round(c.x - w / 2), y: Math.round(c.y - h / 2) }, w, h)
}

export function newMessage(
  s: BoardState,
  me: Actor,
  x: number,
  y: number,
  text: string,
  about: { anchor?: Anchor; replyTo?: string } = {},
): ItemOf<'message'> {
  return {
    ...base(s, me, x, y, MESSAGE_W, 60),
    kind: 'message',
    data: {
      text,
      author: me,
      sessionId: null,
      replyTo: about.replyTo ?? null,
      anchor: about.anchor ?? null,
    },
  }
}

/** Longest title a card made from a message gets; longer first sentences are shortened. */
export const CARD_TITLE_MAX = 40

/** Markdown links and embeds: `[label](href)` / `![label](href)`. */
const LINK = /!?\[([^\]]*)\]\(([^)\s]*)\)/g

/**
 * A card's markdown from a message: its first sentence as the title (with any
 * links in it), the rest as the body. A first sentence too long to be a title
 * gives a shortened plain title, and the body keeps the whole message, so no
 * words are lost.
 */
export function messageToCardMarkdown(text: string): string {
  const src = text.trim()
  // Find where the first sentence ends, never inside a link.
  let end = -1
  let i = 0
  LINK.lastIndex = 0
  let link = LINK.exec(src)
  while (i < src.length) {
    if (link && i === link.index) {
      i = link.index + link[0].length
      link = LINK.exec(src)
      continue
    }
    const c = src[i] ?? ''
    if (c === '\n') {
      end = i
      break
    }
    if ('。！？!?'.includes(c) || (c === '.' && /\s/.test(src[i + 1] ?? ' '))) {
      end = i + 1
      break
    }
    i++
  }
  const first = (end < 0 ? src : src.slice(0, end)).trim()
  const rest = (end < 0 ? '' : src.slice(end)).trim()
  const plain = first.replace(LINK, '$1').replace(/\s+/g, ' ')
  if (plain.length <= CARD_TITLE_MAX) return rest ? `# ${first}\n\n${rest}` : `# ${first}`
  const short = `${plain.slice(0, CARD_TITLE_MAX - 1).trimEnd()}…`
  return `# ${short}\n\n${src}`
}

export const SESSION_NODE_W = 240

/** A conversation placed on the board, at `c` (centre) or the nearest free spot. */
export function newSessionNode(
  s: BoardState,
  me: Actor,
  c: { x: number; y: number },
  sessionId: string,
): ItemOf<'session'> {
  const p = spotNear(s, c, SESSION_NODE_W, 56)
  return { ...base(s, me, p.x, p.y, SESSION_NODE_W, 56), kind: 'session', data: { sessionId } }
}

export function newGroup(
  s: BoardState,
  me: Actor,
  rect: { x: number; y: number; w: number; h: number },
  title: string,
): ItemOf<'group'> {
  let minZ = 0
  for (const i of s.items.values()) if (i.kind === 'group' && i.z < minZ) minZ = i.z
  return {
    ...base(s, me, rect.x, rect.y, Math.round(rect.w), Math.round(rect.h)),
    z: minZ - 1,
    kind: 'group',
    data: { title },
  }
}

export function newLink(
  s: BoardState,
  me: Actor,
  x: number,
  y: number,
  url: string,
): ItemOf<'link'> {
  // Reserve room for a preview image; the real height arrives once measured.
  const p = spotNear(s, { x, y }, LINK_W, 260)
  return { ...base(s, me, p.x, p.y, LINK_W, 90), kind: 'link', data: { url } }
}

export type UploadedFile = { fileKey: string; name: string; mime: string; size: number }

export function newFileItem(
  s: BoardState,
  me: Actor,
  x: number,
  y: number,
  file: UploadedFile,
  natural?: { w: number; h: number },
): Item {
  if (file.mime.startsWith('image/') && natural) {
    const w = Math.min(360, natural.w)
    const h = Math.round((natural.h / natural.w) * w)
    const p = spotNear(s, { x, y }, w, h)
    return {
      ...base(s, me, p.x, p.y, w, h),
      kind: 'image',
      data: { ...file, naturalW: natural.w, naturalH: natural.h },
    }
  }
  const p = spotNear(s, { x, y }, FILE_W, 64)
  return { ...base(s, me, p.x, p.y, FILE_W, 64), kind: 'file', data: file }
}

/** Bounding box of items, padded, for wrapping a selection in a group. */
export function boundsOf(items: Item[], pad = 32) {
  const x = Math.min(...items.map((i) => i.x)) - pad
  const y = Math.min(...items.map((i) => i.y)) - pad - 24
  const r = Math.max(...items.map((i) => i.x + i.w)) + pad
  const b = Math.max(...items.map((i) => i.y + i.h)) + pad
  return { x, y, w: r - x, h: b - y }
}

export const MATERIAL_W = 280

/** A material placed on the board as a handle, at `c` or the nearest free spot. */
export function newMaterialItem(
  s: BoardState,
  me: Actor,
  c: { x: number; y: number },
  material: Material,
): ItemOf<'material'> {
  const p = spotNear(s, c, MATERIAL_W, 80)
  return {
    ...base(s, me, p.x, p.y, MATERIAL_W, 80),
    kind: 'material',
    data: { materialId: material.id, name: material.name },
  }
}

/**
 * A clip of a material's page. Placed below the newest clip of the same
 * material, else beside the material's handle, else near `fallback`.
 */
export function newClip(
  s: BoardState,
  me: Actor,
  material: Material,
  page: number,
  rect: PageRect,
  text: string,
  fallback: { x: number; y: number },
  /** What it points at inside `rect`, when that is less than all of it. */
  focus?: PageRect[],
): ItemOf<'clip'> {
  const p = material.pages[page - 1]
  const size = p ? clipSize(p, rect) : { w: 320, h: 240 }
  let newest: Item | null = null
  let handle: Item | null = null
  for (const i of s.items.values()) {
    if (i.kind === 'clip' && i.data.materialId === material.id) {
      if (!newest || i.createdAt > newest.createdAt) newest = i
    } else if (i.kind === 'material' && i.data.materialId === material.id) handle = i
  }
  const want = newest
    ? { x: newest.x, y: newest.y + newest.h + 24 }
    : handle
      ? { x: handle.x + handle.w + 40, y: handle.y }
      : { x: Math.round(fallback.x - size.w / 2), y: Math.round(fallback.y - size.h / 2) }
  const spot = freeSpotNear(s, want, size.w, size.h)
  return {
    ...base(s, me, spot.x, spot.y, size.w, size.h),
    kind: 'clip',
    data: {
      materialId: material.id,
      page,
      rect,
      ...(focus?.length && { focus: { rects: focus } }),
      ...(text && { text }),
    },
  }
}
